import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { useDevice, useViewport } from '@treatwell/ui';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { createBrowseAggregations } from 'js/model/view/browse-page/browse-aggregations';
import { CmsPageTitle } from 'js/pages/BrowsePage/BrowseTitle';
import { Hero, HeroHandle } from 'js/pages/BrowsePage/WhatWhereWhen/Hero';
import { ViewProps } from 'js/pages/BrowsePage/BrowsePageInner';
import { LoadingButton } from 'js/components/Button/LoadingButton';
import { ButtonColour } from 'js/components/Button';
import {
  Category,
  wwwTrackingContext,
} from 'js/pages/BrowsePage/WhatWhereWhen/tracking';
import { MapVenue } from 'js/model/view/map-view/MapVenue';
import { BrowsePageMap } from 'js/pages/BrowsePage/MapView/BrowsePageMap';
import { CmsVenuePage } from 'js/model/cms/cms-venue-page';
import { getMapVenues } from 'js/components/LeafletMap/map-utilities';
import { Context } from 'js/components/LocaleWrapper';
import { Channel } from 'js/model/rainbow/content/ChannelOutput';
import browseStyle from 'js/pages/BrowsePage/BrowsePage.module.css';
import { BrowsePageList } from './BrowsePageList';
import { ResultsSummary } from './ResultsSummary';
import { trackHighlightMapPin } from './MapView/tracking';
import { ViewMode } from './ViewModeToggle';
import { FiltersBar } from './FilterModal/FiltersBar';
import { changeBrowseDataContext } from './BrowseData';
import stickyCollapseStyles from './WhatWhereWhen/StickyCollapse.module.css';
import styles from './HybridView.module.css';

const collapseActionName = 'map-user-action';

interface Props {
  listOnly?: boolean;
}

export const HybridView = ({
  browsePageOutput,
  viewMode,
  onViewModeChange,
  onAnySelectFocus,
  loading,
  listOnly,
  hasViewModeChanged,
}: ViewProps & Props) => {
  const { pageData, channel } = useContext(Context);
  const changeBrowseData = useContext(changeBrowseDataContext);
  const heroChildRef = useRef<HeroHandle>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const viewModeRef = useRef<ViewMode>(viewMode);
  const { isMobile: isMobileDevice } = useDevice();
  const isMobile = useViewport({
    device: 'mobile',
    serverRender: isMobileDevice,
  });
  const cms = pageData.cms as CmsBrowsePage & CmsPageTitle & CmsVenuePage;

  const [height, setHeight] = useState<number | undefined>(0);
  const [hoveredMapVenue, setHoveredMapVenue] = useState<
    MapVenue | undefined
  >();
  const [renderMap, setRenderMap] = useState(false);
  const [isInHybridView, setIsInHybridView] = useState(false);
  // This feature is currently disabled as part of Related Treatments testing

  // const scrollDirection = useScrollDirection();
  // As part of the Related Treatments experiment we want to hide the sticky header
  // when scrolling down
  // const heroHidden =
  //   pageData.showRelatedTreatments && scrollDirection === 'down';
  const heroHidden = false;

  // We need to manually set padding aruond the map when in Hybrid view
  const mapPadding = isInHybridView ? '16px' : 0;

  const aggregations = useMemo(
    () =>
      createBrowseAggregations(
        browsePageOutput.aggregations,
        channel as Channel,
        pageData.pageParameters
      ),
    [browsePageOutput, channel, pageData.pageParameters]
  );
  const containerClassnames = clsx(styles.container, {
    [styles.listOnly]: viewMode === ViewMode.List,
  });

  const updateHeroHeight = useCallback((height?: number) => {
    setHeight(height);
  }, []);

  const onVenueEnter = useCallback(
    (venueId: number, rating: number) => {
      const mapVenues = getMapVenues(browsePageOutput);
      const mapVenue = mapVenues.find((venue) => venue.id === venueId);

      setHoveredMapVenue(mapVenue);

      if (isInHybridView) {
        trackHighlightMapPin(venueId.toString(), rating);
      }
    },
    [browsePageOutput, isInHybridView]
  );

  const onVenueLeave = useCallback(() => {
    setHoveredMapVenue(undefined);
  }, []);

  const mapShouldRender = useCallback(() => {
    if (listOnly) {
      return false;
    }
    const mapContainerBounds = mapContainerRef.current?.getBoundingClientRect();
    const mapContainerVisible =
      mapContainerBounds?.width !== 0 && mapContainerBounds?.height !== 0;

    return mapContainerVisible;
  }, [listOnly]);

  useEffect(() => {
    setIsInHybridView(mapShouldRender());
    setHeight(
      listOnly ? 0 : heroChildRef.current?.getHeightRef().current?.offsetHeight
    );
    setRenderMap(mapShouldRender());
  }, [mapShouldRender, listOnly]);

  useEffect(() => {
    const onResize = () => {
      setIsInHybridView(mapShouldRender());

      if (!renderMap && mapShouldRender()) {
        setRenderMap(true);
      }
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [renderMap, mapShouldRender]);

  useEffect(() => {
    if (hasViewModeChanged) {
      setIsInHybridView(mapShouldRender());

      if (!renderMap && mapShouldRender()) {
        setRenderMap(true);
      }
    }
  }, [hasViewModeChanged, renderMap, mapShouldRender]);

  useEffect(() => {
    if (viewModeRef.current !== viewMode) {
      setIsInHybridView(mapShouldRender());

      viewModeRef.current = viewMode;
    }
  }, [viewMode, mapShouldRender]);

  return (
    <>
      <wwwTrackingContext.Provider
        value={isInHybridView ? Category.Hybrid : Category.List}
      >
        <Hero
          ref={heroChildRef}
          pageData={pageData}
          cms={cms}
          browsePageOutput={browsePageOutput}
          aggregations={aggregations}
          viewMode={viewMode}
          openOnMount={isMobile}
          collapseOnEvent={collapseActionName}
          onViewModeChange={onViewModeChange}
          onAnySelectFocus={onAnySelectFocus}
          updateHeroHeight={updateHeroHeight}
          hidden={heroHidden}
        />
      </wwwTrackingContext.Provider>

      <div className={styles.wrapper}>
        <FiltersBar
          aggregations={browsePageOutput.aggregations}
          searchOptions={browsePageOutput.searchOptions}
          specification={browsePageOutput.specification}
        />
        <div className={containerClassnames}>
          <div className={styles.summaryAndFilterContainer}>
            <ResultsSummary
              cms={cms}
              resultCount={browsePageOutput.pagination.totalElements}
              specification={browsePageOutput.specification}
            />
          </div>
          <div className={styles.venuesContainer}>
            <div className={styles.listContainer}>
              <BrowsePageList
                browsePageOutput={browsePageOutput}
                loading={loading}
                onVenueEnter={onVenueEnter}
                onVenueLeave={onVenueLeave}
                isInHybridView={isInHybridView}
              />
            </div>

            <div
              className={clsx(
                styles.mapContainer,
                // Match the animation of the hero for a smooth change in height
                // if it is hidden on scroll
                stickyCollapseStyles.heroTransition,
                {
                  [styles.noMap]: listOnly,
                }
              )}
              style={
                heroHidden
                  ? { top: mapPadding, height: `calc(100vh - ${mapPadding})` }
                  : {
                      top: `calc(${height}px + ${mapPadding})`,
                      height: `calc(100vh - ${height}px - ${mapPadding})`,
                    }
              }
              ref={mapContainerRef}
            >
              {renderMap && (
                <BrowsePageMap
                  venues={getMapVenues(browsePageOutput)}
                  changeBrowseData={changeBrowseData}
                  location={browsePageOutput.specification.location}
                  hoveredMapVenue={hoveredMapVenue}
                  cms={cms}
                  channel={channel}
                  mapView="desktop"
                />
              )}
            </div>
          </div>
        </div>

        {loading && (
          <div className={browseStyle.loadingOverlay}>
            {loading && (
              <div className={styles.loadingIndicator}>
                <LoadingButton
                  staticLoading
                  colour={ButtonColour.Blue}
                  label=""
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
